import type { Cell, Row, RowData } from "@tanstack/react-table";
import dayjs from "dayjs";
import { isEqual } from "lodash";
import type { DateRange } from "react-day-picker";

export const getCellRowSpan = <TData>({
  cell,
  previousCells,
  rows,
  rowIndex,
}: {
  rows: Array<Row<TData>>;
  cell: Cell<TData, unknown>;
  previousCells: Array<Cell<TData, unknown>> | undefined;
  rowIndex: number;
}) => {
  let rowSpan;
  if (cell.column.columnDef.meta?.enableRowSpan) {
    const value = cell.getValue();
    const previousRow = rows[rowIndex - 1];
    const previousValue = previousRow?.getValue(cell.column.id);
    const previousCell = previousCells?.[previousCells?.length - 1];

    // If previous cell is not spanned, this can't be spanned
    if (previousValue === value && (!previousCell || previousCell.isSpanned)) {
      cell.isSpanned = true;
      return null;
    }
    rowSpan = 1;
    const previousCellValues = previousCells?.map((c) => c.getValue());
    const maxRowSpannedIndex =
      previousCell && previousCell.rowSpan
        ? rowIndex + previousCell.rowSpan
        : undefined;

    for (const nextRow of rows.slice(rowIndex + 1, maxRowSpannedIndex)) {
      const nextRowpreviousCellsValues = previousCells?.map((c) =>
        nextRow.getValue(c.column.id)
      );
      const nextValue = nextRow.getValue(cell.column.id);

      // If previous cells are not spanned together we are not spanning this one
      if (
        !isEqual(previousCellValues, nextRowpreviousCellsValues) ||
        nextValue !== value
      )
        break;
      rowSpan += 1;
    }
  }
  cell.rowSpan = rowSpan;
  return rowSpan;
};

export const DEFAULT_FILTER_IS_OPEN = true;

export const datesFilterFn = <TData extends RowData>(
  row: Row<TData>,
  columnId: string,
  filterValue: unknown,
  _?: unknown
) => {
  if (!filterValue) return true;
  if (
    filterValue &&
    (typeof filterValue !== "object" ||
      !("from" in filterValue || "to" in filterValue))
  )
    throw new Error("Invalid dates filter value for column " + columnId);
  const value = row.getValue(columnId);
  if (!value) return false;
  const date = dayjs(value as string);
  if (!date.isValid()) throw new Error("Invalid date value for row" + row.id);
  const { from, to } = filterValue as DateRange;
  if (!from && !to) return true;
  const isSameOrAfter = from ? date.isSameOrAfter(dayjs(from)) : true;
  const isSameOrBefore = to ? date.isSameOrBefore(dayjs(to)) : true;
  return isSameOrAfter && isSameOrBefore;
};

export const selectFilterFn = <TData extends RowData>(
  row: Row<TData>,
  columnId: string,
  filterValue: unknown,
  _?: unknown
) => {
  if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0))
    return true;
  if (!Array.isArray(filterValue))
    throw new Error("Invalid select filter value for column " + columnId);
  const value = row.getValue(columnId);
  if (value === undefined) return false;
  return filterValue.includes(value);
};

const EMPTY_ARRAY: Array<unknown> = [];

// React table seems to make reference comparaison which might create infinite loop causing crash.
export const resolveData = <T>(data: Array<T> | undefined): Array<T> =>
  data && data.length > 0 ? data : (EMPTY_ARRAY as Array<T>);
